// MARK://dhk8/storefront/pages/QueuePage
// Lane #163 — comensal se anota a la fila de espera desde su teléfono.
//
// URL: /queue/:tenantSlug (slug humano, no UUID — más amigable para QR).
// queue.nintaigo.com → storefront → /queue/savis-coffee-gdl
//
// El endpoint backend POST /v1/floor/public/waitlist:
//   - Es rate-limited (5/IP/10min)
//   - Resuelve slug → tenantId
//   - Crea PosWaitlistEntry con status=WAITING, queueCode auto-generado
//   - Devuelve { code, position, peopleAhead, estimatedWaitMin, status }
//
// El comensal anota su queueCode (ej. "Q12") y puede consultar después en
// /consulta/:slug para ver su posición actualizada.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandHeader from '../components/BrandHeader.js'
import BusinessIdentity from '../components/BusinessIdentity.js'
import SiteFooter from '../components/SiteFooter.js'
import { useTenantBranding } from '../lib/branding.js'

/** Turno guardado en el dispositivo: sobrevive al refresh. */
const FILA_KEY = 'nintaigo-fila'

const API_BASE =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ??
  '/v1'

interface JoinResponse {
  name: string
  partySize: number
  joinedAt: string
  status: 'WAITING' | 'NOTIFIED'
  position: number
  peopleAhead: number
  estimatedWaitMin: number | null
  code: string | null
  tenantSlug: string
}

interface TenantInfo {
  tenantId: string | null
  displayName: string
}

async function fetchTenant(slug: string): Promise<TenantInfo> {
  if (!/^[a-z0-9-]{2,80}$/.test(slug)) return { tenantId: null, displayName: 'NintaiGO' }
  try {
    const res = await fetch(
      `${API_BASE}/dhk8-platform-tenants/v1/tenants/by-slug/${encodeURIComponent(slug)}`,
      { headers: { accept: 'application/json' } },
    )
    if (!res.ok) return { tenantId: null, displayName: 'NintaiGO' }
    const body = (await res.json()) as { ok: true; data: { tenantId: string; name?: string | null } } | { ok: false }
    if (!('ok' in body) || !body.ok) return { tenantId: null, displayName: 'NintaiGO' }
    return { tenantId: body.data.tenantId, displayName: body.data.name ?? 'NintaiGO' }
  } catch {
    return { tenantId: null, displayName: 'NintaiGO' }
  }
}

export default function QueuePage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const validSlug = !!tenantSlug && /^[a-z0-9-]{2,80}$/.test(tenantSlug)

  // Branding por slug — usa el tenant resuelto si está disponible.
  const [resolvedTenantId, setResolvedTenantId] = useState<string | null>(null)
  useEffect(() => {
    if (!validSlug || !tenantSlug) return
    let alive = true
    void fetchTenant(tenantSlug).then((info) => {
      if (alive) setResolvedTenantId(info.tenantId)
    })
    return () => { alive = false }
  }, [tenantSlug, validSlug])
  const branding = useTenantBranding(resolvedTenantId)

  useEffect(() => {
    const dn = branding.displayName && branding.displayName.toLowerCase() !== 'nintaigo' ? branding.displayName : null
    document.title = `Únete a la fila${dn ? ` · ${dn}` : ''} — NintaiGO`
  }, [branding.displayName])

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [notes, setNotes] = useState('')
  // ── Sucursal ─────────────────────────────────────────────────────────
  // Los enlaces publicos son del tenant; sin esto la reserva/turno nacia sin
  // sucursal y aparecia en todos los locales por igual.
  const [sucursales, setSucursales] = useState<{ branchId: string; name: string }[]>([])
  const [branchId, setBranchId] = useState<string>('')

  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!validSlug) return
    let vivo = true
    void (async () => {
      try {
        const r = await fetch(`${API_BASE}/dhk8-pos-floor/v1/floor/public/branches/${encodeURIComponent(tenantSlug)}`)
        if (!r.ok) return
        const b = (await r.json()) as { ok: boolean; data?: { items?: { branchId: string; name: string }[] } }
        if (!vivo || !b.ok) return
        const items = b.data?.items ?? []
        setSucursales(items)
        if (items.length > 0) setBranchId((prev) => (prev === '' ? items[0]!.branchId : prev))
      } catch { /* sin sucursales -> se forma sin especificar */ }
    })()
    return () => { vivo = false }
  }, [tenantSlug, validSlug])
  const [err, setErr] = useState<string | null>(null)
  const [success, setSuccess] = useState<JoinResponse | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validSlug || !tenantSlug) {
      setErr('Restaurante no identificado en la URL')
      return
    }
    if (!name.trim()) { setErr('Por favor escribe tu nombre'); return }
    if (!phone.trim()) { setErr('Por favor escribe tu teléfono'); return }
    setBusy(true)
    setErr(null)
    try {
      const res = await fetch(`${API_BASE}/dhk8-pos-floor/v1/floor/public/waitlist`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          tenantSlug,
          name: name.trim(),
          phone: phone.trim(),
          partySize,
          ...(branchId ? { branchId } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        }),
      })
      const body = (await res.json()) as { ok: true; data: JoinResponse } | { ok: false; error: { message: string } }
      if (!res.ok || !body.ok) {
        const msg = !body.ok ? body.error.message : `Error ${res.status}`
        setErr(msg)
        return
      }
      setSuccess(body.data)
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error de red')
    } finally {
      setBusy(false)
    }
  }

  // ── Turno persistente ─────────────────────────────────────────────────
  // El turno vivia solo en memoria: un refresh y el comensal perdia su codigo
  // y su posicion, sin forma de saber si ya lo habian llamado.
  useEffect(() => {
    if (success?.code) {
      try { localStorage.setItem(FILA_KEY, JSON.stringify({ slug: tenantSlug, code: success.code })) } catch { /* privado */ }
    }
  }, [success, tenantSlug])

  // Rehidratar al cargar + sondear mientras el turno siga vivo, para que
  // "te llamaron" aparezca sin que el comensal tenga que hacer nada.
  useEffect(() => {
    if (!validSlug) return
    let vivo = true
    const leerGuardado = (): string | null => {
      try {
        const raw = localStorage.getItem(FILA_KEY)
        if (!raw) return null
        const g = JSON.parse(raw) as { slug?: string; code?: string }
        return g.slug === tenantSlug && typeof g.code === 'string' ? g.code : null
      } catch { return null }
    }
    const consultar = async (): Promise<void> => {
      const code = success?.code ?? leerGuardado()
      if (code === null || code === undefined) return
      try {
        const r = await fetch(
          `${API_BASE}/dhk8-pos-floor/v1/floor/public/waitlist/by-code/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(code)}`,
        )
        if (!r.ok) return
        const body = (await r.json()) as { ok: boolean; data?: Partial<JoinResponse> & { status?: string } }
        if (!vivo || !body.ok || !body.data) return
        const d = body.data
        // Turno cerrado (sentado / se fue / no llego) → se limpia para que la
        // proxima visita no reviva un turno muerto.
        if (d.status === 'SEATED' || d.status === 'LEFT' || d.status === 'NO_SHOW') {
          try { localStorage.removeItem(FILA_KEY) } catch { /* privado */ }
          return
        }
        setSuccess((prev) => ({
          name: d.name ?? prev?.name ?? '',
          partySize: d.partySize ?? prev?.partySize ?? 0,
          joinedAt: d.joinedAt ?? prev?.joinedAt ?? '',
          status: (d.status === 'NOTIFIED' ? 'NOTIFIED' : 'WAITING'),
          position: d.position ?? prev?.position ?? 0,
          peopleAhead: d.peopleAhead ?? prev?.peopleAhead ?? 0,
          estimatedWaitMin: d.estimatedWaitMin ?? prev?.estimatedWaitMin ?? null,
          code: code,
          tenantSlug,
        }))
      } catch { /* red intermitente: se reintenta en el siguiente tick */ }
    }
    void consultar()
    const iv = setInterval(() => { void consultar() }, 10_000)
    return () => { vivo = false; clearInterval(iv) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, validSlug, success?.code])

  if (!validSlug) {
    return (
      <div className="sf-page">
        <BrandHeader branding={branding} platformBrand />
        <div className="sf-form-page">
          <div className="sf-card">
            <BusinessIdentity branding={branding} />
            <h1 className="sf-card__title">Fila de espera</h1>
            <p className="sf-card__lede">
              La URL no incluye un restaurante válido. Verifica el código QR del local.
            </p>
          </div>
        </div>
        <SiteFooter />
      </div>
    )
  }

  if (success) {
    return (
      <div className="sf-page">
        <BrandHeader branding={branding} platformBrand />
        <div className="sf-form-page">
          <div className="sf-card sf-card--success">
            <BusinessIdentity branding={branding} />
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 56, lineHeight: 1 }}>{success.status === 'NOTIFIED' ? '🔔' : '🎟️'}</div>
              <h1 className="sf-card__title" style={{ marginTop: 12 }}>
                {success.status === 'NOTIFIED' ? '¡Es tu turno!' : '¡Estás en la fila!'}
              </h1>
              {success.status === 'NOTIFIED' && (
                <p style={{ marginTop: 6, fontWeight: 600 }}>Tu mesa está lista — pasa con el anfitrión.</p>
              )}
            </div>

            {success.code && (
              <div style={{ background: 'var(--sf-tint-bg, rgba(76,175,80,0.12))', border: '2px solid var(--sf-tint, #4caf50)', borderRadius: 12, padding: '16px 12px', textAlign: 'center', margin: '16px 0' }}>
                <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.7 }}>Tu código</div>
                <div style={{ fontSize: 48, fontWeight: 700, fontFamily: 'monospace', color: 'var(--sf-tint, #4caf50)', letterSpacing: '0.1em' }}>
                  {success.code}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px 0' }}>
              <div className="sf-stat">
                <span className="sf-stat__label">Tu posición</span>
                <span className="sf-stat__value">#{success.position}</span>
              </div>
              <div className="sf-stat">
                <span className="sf-stat__label">Personas adelante</span>
                <span className="sf-stat__value">{success.peopleAhead}</span>
              </div>
              {success.estimatedWaitMin !== null && (
                <div className="sf-stat" style={{ gridColumn: '1 / -1' }}>
                  <span className="sf-stat__label">Tiempo estimado</span>
                  <span className="sf-stat__value">~{success.estimatedWaitMin} min</span>
                </div>
              )}
            </div>

            <p style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, opacity: 0.75 }}>
              {success.status === 'NOTIFIED'
                ? 'Esta pantalla se actualiza sola. Guarda tu código por si necesitas consultarlo:'
                : 'Te avisaremos cuando tu mesa esté lista — esta pantalla se actualiza sola. También puedes consultar tu estado con tu código:'}
            </p>
            <p style={{ textAlign: 'center' }}>
              <a href={`/consulta/${success.tenantSlug}?tipo=fila&code=${encodeURIComponent(success.code ?? '')}`} className="sf-btn--ghost">
                Consultar mi estado
              </a>
            </p>
          </div>
        </div>
        <SiteFooter />
      </div>
    )
  }

  return (
    <div className="sf-page">
      <BrandHeader branding={branding} platformBrand />
      <div className="sf-form-page">
        <form className="sf-card" onSubmit={(e) => void handleSubmit(e)}>
          <BusinessIdentity branding={branding} />
          <h1 className="sf-card__title">Únete a la fila</h1>
          <p className="sf-card__lede">
            Anótate desde aquí. Te daremos un código y te diremos tu posición — recibirás aviso cuando tu mesa esté lista.
          </p>

          {sucursales.length > 1 && (
            <label className="sf-field">
              <span className="sf-field__label">Sucursal</span>
              <select className="sf-input" value={branchId} onChange={(e) => setBranchId(e.target.value)} required>
                {sucursales.map((b) => <option key={b.branchId} value={b.branchId}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className="sf-field">
            <span className="sf-field__label">Tu nombre</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              placeholder="¿Cómo te llamamos?"
              className="sf-input"
            />
          </label>

          <label className="sf-field">
            <span className="sf-field__label">Teléfono (WhatsApp)</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="+52 33..."
              className="sf-input"
            />
          </label>

          <label className="sf-field">
            <span className="sf-field__label">¿Cuántas personas?</span>
            <input
              type="number"
              min={1}
              max={50}
              value={partySize}
              onChange={(e) => setPartySize(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              className="sf-input"
            />
          </label>

          <label className="sf-field">
            <span className="sf-field__label">Notas (opcional)</span>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Mesa en terraza, niño..."
              className="sf-input"
            />
          </label>

          {err && <div className="sf-error" role="alert">{err}</div>}

          <button type="submit" className="sf-btn-primary" disabled={busy}>
            {busy ? 'Anotando…' : 'Unirme a la fila'}
          </button>

          <p className="sf-footnote" style={{ textAlign: 'center' }}>
            ¿Ya estás en la fila? <a href={`/consulta/${tenantSlug}`}>Consulta tu estado</a>
          </p>
        </form>
      </div>
      <SiteFooter />

      <style>{`
        .sf-card--success { background: var(--sf-surface, #fff); border: 2px solid var(--sf-tint, #4caf50); }
        .sf-stat { display: flex; flex-direction: column; gap: 4px; padding: 12px; background: var(--sf-canvas, #f7f7f5); border: 1px solid var(--sf-border, #e0e0db); border-radius: 8px; text-align: center; }
        .sf-stat__label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--sf-muted, #666); }
        .sf-stat__value { font-size: 24px; font-weight: 600; font-family: monospace; color: var(--sf-ink, #1a1a1a); }
        .sf-btn--ghost { display: inline-block; padding: 10px 20px; background: transparent; border: 1px solid var(--sf-border, #e0e0db); color: inherit; text-decoration: none; border-radius: 6px; font-family: inherit; cursor: pointer; }
        .sf-btn--ghost:hover { background: var(--sf-canvas, #f7f7f5); }
      `}</style>
    </div>
  )
}
